import { watch } from 'vue';
import { toValue } from '@vueuse/core';
import addItemToBag from '@nuxt-common/add-to-bag/app/composables/useAddToBag';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useMutation } from '~/composables/utils/useMutation';
import { usePageData } from '~/composables/usePageData';
import { useProductType } from '~/composables/useProductType';
import getAnalyticsProductTypeData from '~/utils/analytics';
import { ExpressCheckoutService } from '../services/ExpressCheckout.service';
import { ATBError } from '../errors/ATBError';
import { ATBValidationError } from '../errors/ATBValidationError';
import { tagATBValidationError } from '../features/analytics/ATBValidationError';
import useStickyAtbAnalytics from '~/modules/sticky-product-traits/composables/useStickyAtbAnalytics';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';

/** @typedef {import('../types').ATBPayload} ATBPayload */
/** @typedef {import('../types').ATBPayloadValidator} ATBPayloadValidator */
/** @typedef {import('../types').ATBFlags} ATBFlags */

/**
 * * Composable that handles the Add To Bag (ATB) functionality
 * Main purpose of this composable is to provide a consistent component API for the ATB functionality,
 * and to handle the ATB errors in a consistent way.
 *
 * @description
 * This composable is a wrapper around the ATB store module.
 * It provides a function that triggers the ATB action and returns the bag object.
 * It also provides a color and size validation.
 *
 * @see {@link https://confluence.federated.fds/display/CAPPD/Vue+Add+to+Bag+flow+with+component%27s+technical+details}
 *
 * @param {Object} payload - The product id.
 * @param {import('@vueuse/shared').MaybeRefOrGetter<ATBPayload>} [payload.payload] - The product id.
 * @param {ATBPayloadValidator} [payload.payloadValidator] - payload validation function
 * @param {ATBFlags} flags - ATB flags
 * @param {import('~/composables/utils/useMutation').MutationCallbacks<any>} [callbacks] - The callbacks object.
 * The callback function that will be called after the ATB action is triggered.
 */
export function useAddToBag({ payload, payloadValidator = () => null } = {}, { checkoutRedirect = false } = {}, callbacks) {
  if (!payload) {
    throw new ATBError('payload is required');
  }

  const pageData = usePageData();
  const productTypeObj = useProductType();
  const { isMcom = true } = toValue(payload)?.flags || {};
  const {
    killswitches: { isStartCheckoutPostCallEnabled },
  } = useKillswitches();
  const { extraBagData } = useStickyAtbAnalytics();
  const expressCheckoutService = new ExpressCheckoutService({ isStartCheckoutPostCallEnabled });

  const selectedPrice = useProductPrice();

  const getPromoMessaging = () => {
    const offer = selectedPrice.value?.badgesList?.[0];

    return offer?.promoId ? [offer.promoId] : ['no promo messaging'];
  };

  /**
   * * Adds Payload to bag (with validation)
   *
   * @description
   * If the validation fails, an error will be thrown.
   * And the main advantage of this composable is to provide a consistent way to handle the ATB errors.
   * That's why we need to validate the payload on our own.
   */
  const addToBag = async ({ isUpdate = false, buynow = false, isAddAnotherToBag = false } = {}) => {
    const sourcePayload = toValue(payload);
    const errorMessage = await payloadValidator(sourcePayload);
    const typeData = getAnalyticsProductTypeData(productTypeObj);

    if (errorMessage) {
      throw new ATBValidationError(errorMessage);
    }
    const sourceItem = sourcePayload?.data?.item;
    const payloadValue = {
      ...sourcePayload,
      data: {
        ...sourcePayload?.data,
        ...(sourceItem ? { item: { ...sourceItem } } : {}),
      },
      options: {
        ...(sourcePayload?.options || {}),
        sourceUtagData: {
          ...(sourcePayload?.options?.sourceUtagData || {}),
        },
      },
    };

    if (!isUpdate) {
      delete payloadValue.options.sequenceNumber;
    }

    if (isAddAnotherToBag && payloadValue.data?.item) {
      payloadValue.data.item.quantity = 1;
    }

    if (buynow && payloadValue.data?.item) {
      payloadValue.data.item.buyNow = buynow;
    }

    // TODO merge payload
    payloadValue.options.sourceUtagData = {
      ...payloadValue.options.sourceUtagData,
      buyNow: buynow,
      consolidated_product: typeData.consolidated_product,
      product_type: typeData.product_type || pageData.product?.analytics?.data?.product_type,
      master_pdp_type: typeData.master_pdp_type || '',
      product_promo_messaging: getPromoMessaging(),
      ...extraBagData(),
    };

    try {
      const result = await addItemToBag(payloadValue.data, {
        useModal: isMcom,
        isUpdate,
        ...payloadValue.options,
      });
      checkoutRedirect && (await expressCheckoutService.checkout());
      return result;
    } catch (e) {
      if (e instanceof Error) {
        throw new ATBError(e.message);
      }
      console.error(e);
    }
  };

  const addToBagMutation = useMutation(addToBag, {
    key: 'add_to_bag_mutation',
    resetOnPageChange: true,
    /**
     * @param {any} data
     */
    ...callbacks,
    onError(error) {
      callbacks?.onError?.(error);
      if (error instanceof ATBValidationError) {
        tagATBValidationError(error?.message);
      }
    },
  });

  watch(addToBagMutation.isLoading, (newValue) => {
    if (!newValue) return;
    addToBagMutation.data.value = null;
    addToBagMutation.resetError();
  });

  watch(() => toValue(payload), addToBagMutation.resetError);

  return addToBagMutation;
}

export default useAddToBag;
